import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const MachineCard: React.FC<{
  title: string;
  img: string;
  desc: string;
  features: string[];
  tag: string;
  index: number;
}> = ({ title, img, desc, features, tag, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });
    
    // Parallax effect for image
    const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);
    const scale = useTransform(scrollYProgress, [0.2, 0.8], [0.95, 1]);

  return (
    <motion.div 
      ref={ref}
      className="sticky top-24 w-full bg-[#050505] rounded-[40px] overflow-hidden border border-white/10 mb-20 shadow-2xl origin-top"
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.8 }}
      style={{ 
        // Dynamic stacking effect based on scroll index can be complex, sticking to simple sticky/reveal
        zIndex: index + 10 
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 h-auto lg:h-[600px]">
        {/* Content Side */}
        <div className="p-6 md:p-8 lg:p-16 flex flex-col justify-center relative z-10">
          <motion.div 
             initial={{ opacity: 0, x: -20 }}
             whileInView={{ opacity: 1, x: 0 }}
             transition={{ delay: 0.3 }}
             className="mb-8"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-[#3D0C23] text-[#FF4D9E] text-sm font-bold border border-[#FF4D9E]/30 tracking-wide uppercase">
              ✦ {tag}
            </span>
          </motion.div>
          
          <motion.h2 
            className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-8 font-medium leading-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {title}
          </motion.h2>
          
          <motion.p 
            className="text-gray-400 text-lg mb-12 leading-relaxed max-w-md"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {desc}
          </motion.p>
          
          <div className="space-y-4">
            {features.map((feature, idx) => (
              <motion.div 
                key={idx} 
                className="flex items-center text-gray-300 text-base group/item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + (idx * 0.1) }}
              >
                 <span className="mr-4 text-[#CA1167] group-hover/item:scale-125 transition-transform duration-300">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                       <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                       <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                 </span>
                 <span className="group-hover/item:text-white transition-colors duration-300">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Image Side */}
        <div className="relative h-[400px] lg:h-full bg-[#111] overflow-hidden group">
             
             
             <motion.img 
               src={img} 
               alt={title} 
               className="w-full h-full object-cover object-center"
               style={{ y, scale }}
             />
             
             {/* Tech Overlay Lines */}
             <div className="absolute inset-0 opacity-20 pointer-events-none z-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>
             
             {/* Logo watermark */}
             <div className="absolute bottom-8 right-8 text-white/10 font-heading text-4xl font-bold tracking-tighter transform rotate-[-5deg]">
                PrintAura
             </div>
        </div>
      </div>
    </motion.div>
  );
};

export const TechMachines: React.FC = () => {
    // Add scroll progress for the overall section if we want a connecting line
  return (
    <section className="py-16 md:py-32 bg-black px-4 relative">
      <div className="absolute left-[50%] top-0 bottom-0 w-[1px] bg-white/10 hidden lg:block transform -translate-x-1/2" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <MachineCard
          index={0}
          tag="Printing Technology"
          title="High-Precision Offset Printing"
          desc="We use modern, industry-leading offset printing systems designed for crystal-sharp output and consistent color accuracy."
          img="/Images/tech/tech_1.webp"
          features={[
            "Multi-color offset printing",
            "Perfect registration & sharp detailing",
            "High-speed production for large volumes",
            "Advanced ink management systems",
            "Consistency across long-run batches"
          ]}
        />
        
        <MachineCard
          index={1}
          tag="Die-Cutting & Structural Engineering"
          title="Precision Die-Cutting"
          desc="Our automated die-cutting units ensure every carton holds perfect shape and structure."
          img="/Images/tech/die-cutting.webp"
          features={[
            "High-accuracy cutting",
            "Smooth edges & clean lines",
            "Complex shape capability",
            "Fast turnaround time"
          ]}
        />

        <MachineCard
          index={2}
          tag="Folding, Gluing & Assembly"
          title="Automated Folding & Gluing Lines"
          desc="Modern, efficient machines for flawless carton assembly."
          img="/Images/tech/tech_3.webp"
          features={[
            "Straight-line cartons",
            "Lock-bottom/auto-bottom boxes",
            "Multi-panel structures",
            "High-speed continuous production"
          ]}
        />

        <MachineCard
          index={3}
          tag="Premium Finishing Technology"
          title="Premium Finishing Technology"
          desc="We offer modern finishing systems to elevate brand presence and enhance shelf appeal."
          img="/Images/tech/tech_4.webp"
          features={[
            "Spot UV",
            "Embossing / Debossing",
            "Metallic foiling (Gold, Silver, Rose-gold)",
            "Soft-touch & velvet coatings"
          ]}
        />
      </div>
    </section>
  );
};
